import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { useCallback, useEffect, useState } from 'react';

import {
  getApiErrorMessage,
  getMaterialFrequencyReport,
  downloadMaterialFrequencyReport,
} from '../../../../api/mix-deliveriesApi';

const INITIAL_FILTERS = {
  preparedFrom: '',
  preparedTo: '',
  materialSearch: '',
  recipientSearch: '',
  sandwichingRequired: '',
};

const EMPTY_SUMMARY = {
  totalMaterials: 0,
  totalPreparations: 0,
  totalMixPositions: 0,
  totalMixQuantity: 0,
  mostFrequentMaterial: null,
};

const EMPTY_PAGINATION = {
  page: 1,
  limit: 25,
  total: 0,
  totalPages: 0,
};

export default function MixMaterialFrequencyReport({ refreshKey = 0 }) {
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);

  /*
   * MUI numeruje strony od zera,
   * backend od jednego.
   */
  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(50);

  const [rows, setRows] = useState([]);

  const [summary, setSummary] = useState(EMPTY_SUMMARY);

  const [pagination, setPagination] = useState(EMPTY_PAGINATION);

  const [loading, setLoading] = useState(true);

  const [exporting, setExporting] = useState(false);

  const [error, setError] = useState('');

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const result = await getMaterialFrequencyReport({
        ...appliedFilters,

        page: page + 1,
        limit: rowsPerPage,
      });

      setRows(Array.isArray(result?.data) ? result.data : []);

      setSummary({
        totalMaterials: Number(result?.summary?.totalMaterials ?? 0),

        totalPreparations: Number(result?.summary?.totalPreparations ?? 0),

        totalMixPositions: Number(result?.summary?.totalMixPositions ?? 0),

        totalMixQuantity: Number(result?.summary?.totalMixQuantity ?? 0),

        mostFrequentMaterial: result?.summary?.mostFrequentMaterial ?? null,
      });

      setPagination({
        page: Number(result?.pagination?.page ?? page + 1),

        limit: Number(result?.pagination?.limit ?? rowsPerPage),

        total: Number(result?.pagination?.total ?? 0),

        totalPages: Number(result?.pagination?.totalPages ?? 0),
      });
    } catch (loadError) {
      setRows([]);
      setSummary(EMPTY_SUMMARY);
      setPagination(EMPTY_PAGINATION);

      setError(getApiErrorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, page, rowsPerPage]);

  useEffect(() => {
    loadReport();
  }, [loadReport, refreshKey]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;

    setFilters((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleApplyFilters = (event) => {
    event.preventDefault();

    setPage(0);

    setAppliedFilters({
      ...filters,

      materialSearch: filters.materialSearch.trim(),

      recipientSearch: filters.recipientSearch.trim(),
    });
  };

  const handleClearFilters = () => {
    setFilters(INITIAL_FILTERS);

    setAppliedFilters(INITIAL_FILTERS);

    setPage(0);
  };

  const handlePageChange = (_event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(Number(event.target.value));

    setPage(0);
  };

  const handleExport = async () => {
    setExporting(true);
    setError('');

    try {
      await downloadMaterialFrequencyReport(appliedFilters);
    } catch (exportError) {
      setError(getApiErrorMessage(exportError));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h5" fontWeight={700}>
          Częstotliwość materiałów
        </Typography>

        <Typography variant="body2" color="text.secondary">
          Analiza materiałów występujących w faktycznie przygotowanych pozycjach
          miksowych.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper
        component="form"
        variant="outlined"
        onSubmit={handleApplyFilters}
        sx={{
          p: 2,
        }}
      >
        <Stack spacing={2}>
          <Typography variant="subtitle1" fontWeight={700}>
            Filtry
          </Typography>

          <Box
            sx={{
              display: 'grid',

              gridTemplateColumns: {
                xs: '1fr',

                sm: 'repeat(2, minmax(0, 1fr))',

                lg: 'repeat(5, minmax(0, 1fr))',
              },

              gap: 2,
            }}
          >
            <TextField
              name="preparedFrom"
              label="Przygotowano od"
              type="date"
              value={filters.preparedFrom}
              onChange={handleFilterChange}
              InputLabelProps={{
                shrink: true,
              }}
              size="small"
              fullWidth
            />

            <TextField
              name="preparedTo"
              label="Przygotowano do"
              type="date"
              value={filters.preparedTo}
              onChange={handleFilterChange}
              InputLabelProps={{
                shrink: true,
              }}
              size="small"
              fullWidth
            />

            <TextField
              name="materialSearch"
              label="Numer lub nazwa materiału"
              value={filters.materialSearch}
              onChange={handleFilterChange}
              size="small"
              fullWidth
            />

            <TextField
              name="recipientSearch"
              label="Numer lub nazwa odbiorcy"
              value={filters.recipientSearch}
              onChange={handleFilterChange}
              size="small"
              fullWidth
            />

            <FormControl size="small" fullWidth>
              <InputLabel id="material-frequency-sandwiching-label">
                Kanapkowanie
              </InputLabel>

              <Select
                labelId="material-frequency-sandwiching-label"
                name="sandwichingRequired"
                label="Kanapkowanie"
                value={filters.sandwichingRequired}
                onChange={handleFilterChange}
              >
                <MenuItem value="">Wszystkie</MenuItem>

                <MenuItem value="true">Tak</MenuItem>

                <MenuItem value="false">Nie</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
            spacing={1}
            justifyContent="space-between"
          >
            <Stack
              direction={{
                xs: 'column',
                sm: 'row',
              }}
              spacing={1}
            >
              <Button type="submit" variant="contained">
                Zastosuj filtry
              </Button>

              <Button
                type="button"
                variant="outlined"
                onClick={handleClearFilters}
              >
                Wyczyść
              </Button>
            </Stack>

            <Button
              type="button"
              variant="outlined"
              onClick={handleExport}
              disabled={exporting}
              startIcon={exporting ? <CircularProgress size={18} /> : undefined}
            >
              {exporting ? 'Generowanie...' : 'Pobierz Excel'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Box
        sx={{
          display: 'grid',

          gridTemplateColumns: {
            xs: '1fr',

            sm: 'repeat(2, minmax(0, 1fr))',

            lg: 'repeat(4, minmax(0, 1fr))',
          },

          gap: 2,
        }}
      >
        <SummaryCard
          label="Unikalne materiały"
          value={formatInteger(summary.totalMaterials)}
        />

        <SummaryCard
          label="Liczba przygotowań"
          value={formatInteger(summary.totalPreparations)}
        />

        <SummaryCard
          label="Pozycje miksowe"
          value={formatInteger(summary.totalMixPositions)}
        />

        <SummaryCard
          label="Łączna ilość miksowa"
          value={formatQuantity(summary.totalMixQuantity)}
        />
      </Box>

      {summary.mostFrequentMaterial && (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
          }}
        >
          <Stack
            direction={{
              xs: 'column',
              md: 'row',
            }}
            spacing={2}
            alignItems={{
              xs: 'flex-start',
              md: 'center',
            }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="body2" color="text.secondary">
                Najczęściej występujący materiał
              </Typography>

              <Typography variant="h6" fontWeight={700}>
                {String(summary.mostFrequentMaterial.materialNumber)}
                {' — '}
                {summary.mostFrequentMaterial.productName}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                label={`Przygotowania: ${formatInteger(
                  summary.mostFrequentMaterial.preparationCount,
                )}`}
                color="primary"
              />

              <Chip
                label={`Udział: ${formatPercent(
                  summary.mostFrequentMaterial.preparationSharePercent,
                )}`}
                variant="outlined"
              />
            </Stack>
          </Stack>
        </Paper>
      )}

      <Paper variant="outlined">
        <TableContainer>
          <Table
            size="small"
            sx={{
              minWidth: 1650,
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{
                    width: 60,
                  }}
                >
                  #
                </TableCell>

                <TableCell>Materiał</TableCell>

                <TableCell>Nazwa materiału</TableCell>

                <TableCell>JM</TableCell>

                <TableCell align="right">Przygotowania</TableCell>

                <TableCell align="right">Pozycje</TableCell>

                <TableCell align="right">Unikalne dostawy</TableCell>

                <TableCell align="right">Łączna ilość</TableCell>

                <TableCell align="right">Średnia ilość</TableCell>

                <TableCell>Udział w przygotowaniach</TableCell>

                <TableCell>Pierwsze wystąpienie</TableCell>

                <TableCell>Ostatnie wystąpienie</TableCell>

                <TableCell>Dostawy</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={13} align="center">
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="center"
                      sx={{
                        py: 4,
                      }}
                    >
                      <CircularProgress size={22} />

                      <Typography variant="body2" color="text.secondary">
                        Ładowanie raportu...
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}

              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={13} align="center">
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        py: 4,
                      }}
                    >
                      Brak materiałów spełniających wybrane filtry.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {!loading &&
                rows.map((row, index) => {
                  const rankingPosition = page * rowsPerPage + index + 1;

                  return (
                    <TableRow
                      key={[row.materialNumber, row.unit].join('-')}
                      hover
                    >
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {rankingPosition}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" fontWeight={700}>
                          {String(row.materialNumber)}
                        </Typography>
                      </TableCell>

                      <TableCell>{row.productName || '—'}</TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          label={row.unit || '—'}
                          variant="outlined"
                        />
                      </TableCell>

                      <TableCell align="right">
                        {formatInteger(row.preparationCount)}
                      </TableCell>

                      <TableCell align="right">
                        {formatInteger(row.positionCount)}
                      </TableCell>

                      <TableCell align="right">
                        {formatInteger(row.uniqueDeliveryCount)}
                      </TableCell>

                      <TableCell align="right">
                        {formatQuantity(row.totalMixQuantity)}
                      </TableCell>

                      <TableCell align="right">
                        {formatQuantity(row.averageMixQuantity)}
                      </TableCell>

                      <TableCell>
                        <Box
                          sx={{
                            minWidth: 170,
                          }}
                        >
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            spacing={1}
                            sx={{
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {row.preparationCount} z{' '}
                              {summary.totalPreparations}
                            </Typography>

                            <Typography variant="caption" fontWeight={700}>
                              {formatPercent(row.preparationSharePercent)}
                            </Typography>
                          </Stack>

                          <LinearProgress
                            variant="determinate"
                            value={clampPercent(row.preparationSharePercent)}
                            sx={{
                              height: 7,
                              borderRadius: 4,
                            }}
                          />
                        </Box>
                      </TableCell>

                      <TableCell>
                        {formatDateTime(row.firstOccurrenceAt)}
                      </TableCell>

                      <TableCell>
                        {formatDateTime(row.lastOccurrenceAt)}
                      </TableCell>

                      <TableCell>
                        <DeliveryNumbers
                          deliveryNumbers={row.deliveryNumbers}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={pagination.total}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          labelRowsPerPage="Wierszy na stronę:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} z ${count !== -1 ? count : `ponad ${to}`}`
          }
        />
      </Paper>
    </Stack>
  );
}

function SummaryCard({ label, value }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>

      <Typography
        variant="h5"
        fontWeight={700}
        sx={{
          mt: 0.5,
        }}
      >
        {value}
      </Typography>
    </Paper>
  );
}

function DeliveryNumbers({ deliveryNumbers }) {
  const values = Array.isArray(deliveryNumbers) ? deliveryNumbers : [];

  if (values.length === 0) {
    return '—';
  }

  const visible = values.slice(0, 2);

  const remaining = values.length - visible.length;

  const fullText = values.join(', ');

  return (
    <Tooltip title={fullText} placement="top" arrow>
      <Box
        sx={{
          cursor: 'help',
          maxWidth: 230,
        }}
      >
        <Typography variant="body2" noWrap>
          {visible.join(', ')}
        </Typography>

        {remaining > 0 && (
          <Typography variant="caption" color="text.secondary">
            +{remaining} kolejnych
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}

function formatInteger(value) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return '0';
  }

  return new Intl.NumberFormat('pl-PL', {
    maximumFractionDigits: 0,
  }).format(number);
}

function formatQuantity(value) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return '0';
  }

  return new Intl.NumberFormat('pl-PL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(number);
}

function formatPercent(value) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return '0%';
  }

  return `${new Intl.NumberFormat('pl-PL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(number)}%`;
}

function clampPercent(value) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return 0;
  }

  return Math.min(100, Math.max(0, number));
}

function formatDateTime(value) {
  if (!value) {
    return '—';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return new Intl.DateTimeFormat('pl-PL', {
    timeZone: 'Europe/Warsaw',

    year: 'numeric',
    month: '2-digit',
    day: '2-digit',

    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}
